<template>
    <div class="pivot-builder">
        <h3>Dinamik Anket Pivot Matrisi (Satır/Sütun Toplamları Dahil)</h3>

        <!-- KONTROL PANELİ -->
        <div class="control-bar">
            <!-- HESAPLAMA TÜRÜ -->
            <div class="control-group">
                <label><strong>Hesaplama Metriği: </strong></label>
                <select v-model="selectedAggregator">
                    <option value="AVG">Ortalama (AVG)</option>
                    <option value="COUNT">Kayıt Sayısı (COUNT)</option>
                    <option value="SUM">Toplam (SUM)</option>
                    <option value="MAX">En Yüksek (MAX)</option>
                    <option value="MIN">En Düşük (MIN)</option>
                </select>
            </div>

            <!-- SATIR SIRALAMA -->
            <div class="control-group">
                <label><strong>Satır Sıralaması: </strong></label>
                <button class="sort-btn" :class="{ active: rowSortOrder === 'ASC' }" @click="toggleRowSort('ASC')">
                    A-Z ▲
                </button>
                <button class="sort-btn" :class="{ active: rowSortOrder === 'DESC' }" @click="toggleRowSort('DESC')">
                    Z-A ▼
                </button>
                <button v-if="rowSortOrder !== 'NONE'" class="clear-sort-btn" @click="rowSortOrder = 'NONE'">
                    Sıfırla
                </button>
            </div>

            <!-- SÜTUN SIRALAMA -->
            <div class="control-group">
                <label><strong>Sütun Sıralaması: </strong></label>
                <button class="sort-btn" :class="{ active: colSortOrder === 'ASC' }" @click="colSortOrder = 'ASC'">
                    A-Z ▲
                </button>
                <button class="sort-btn" :class="{ active: colSortOrder === 'DESC' }" @click="colSortOrder = 'DESC'">
                    Z-A ▼
                </button>
                <button v-if="colSortOrder !== 'NONE'" class="clear-sort-btn" @click="colSortOrder = 'NONE'">
                    Sıfırla
                </button>
            </div>
        </div>

        <!-- SÜRÜKLE BIRAK PANELLERİ -->
        <div class="builder-panels">

            <!-- MEVCUT ALANLAR HAVUZU -->
            <div class="panel">
                <h4>Tüm Alanlar (Mevcut)</h4>
                <draggable v-model="availableFields" :group="{ name: 'pivot' }" class="drag-area" :animation="200">
                    <div v-for="element in availableFields" :key="element.key" class="field-chip available">
                        ⋮⋮ {{ element.label }}
                    </div>
                </draggable>
            </div>

            <!-- SATIR ALANLARI -->
            <div class="panel">
                <h4>Satır Alanları (Sorular)</h4>
                <draggable v-model="rowFields" :group="{ name: 'pivot' }" class="drag-area dropzone" :animation="200">
                    <div v-for="element in rowFields" :key="element.key" class="field-chip row-chip">
                        ⋮⋮ {{ element.label }}
                    </div>
                </draggable>
                <div v-if="rowFields.length === 0" class="empty-hint">Boş kalırsa tüm sorular genel hesaplanır</div>
            </div>

            <!-- SÜTUN ALANLARI -->
            <div class="panel">
                <h4>Sütun Alanları (Kategoriler)</h4>
                <draggable v-model="colFields" :group="{ name: 'pivot' }" class="drag-area dropzone" :animation="200">
                    <div v-for="element in colFields" :key="element.key" class="field-chip col-chip">
                        ⋮⋮ {{ element.label }}
                    </div>
                </draggable>
                <div v-if="colFields.length === 0" class="empty-hint">Boş kalırsa genel toplam basılır</div>
            </div>

        </div>

        <!-- TABLO ALANI -->
        <div class="table-container">
            <table class="pivot-matrix-table">
                <thead>
                    <!-- 1. ÜST HEADER SATIRI -->
                    <tr>
                        <th class="corner-header" rowspan="2">No</th>
                        <th class="corner-header" rowspan="2">İfade / Soru</th>

                        <th v-for="colGroup in columnHierarchy" :key="colGroup.categoryKey"
                            :colspan="colGroup.options.length" class="top-category-header">
                            {{ colGroup.categoryLabel }}
                        </th>

                        <!-- GENEL TOPLAM SÜTUNU HEADER -->
                        <th class="total-header" rowspan="2">Genel Toplam</th>
                    </tr>

                    <!-- 2. ALT HEADER SATIRI -->
                    <tr>
                        <template v-for="colGroup in columnHierarchy">
                            <th v-for="optValue in colGroup.options" :key="colGroup.categoryKey + '_' + optValue"
                                class="sub-category-header">
                                {{ optValue }}
                            </th>
                        </template>
                    </tr>
                </thead>

                <tbody>
                    <!-- SATIR SATIR DİZİLİM -->
                    <tr v-for="(qField, index) in processedRowFields" :key="qField.key">
                        <td class="no-cell">{{ qField.key === 'ALL_QUESTIONS' ? '-' : index + 1 }}</td>
                        <td class="question-cell"><strong>{{ qField.label }}</strong></td>

                        <!-- KESİŞİM HÜCRELERİ -->
                        <template v-for="colGroup in columnHierarchy">
                            <td v-for="optValue in colGroup.options" :key="colGroup.categoryKey + '_' + optValue"
                                class="data-cell">
                                {{ calculateOptionMetric(qField.key, colGroup.categoryKey, optValue) }}
                            </td>
                        </template>

                        <!-- SATIR GENEL TOPLAMI (SAĞ TARAF) -->
                        <td class="row-total-cell">
                            <strong>{{ calculateOptionMetric(qField.key, 'ALL_CATEGORIES', 'ALL') }}</strong>
                        </td>
                    </tr>

                    <!-- SÜTUN GENEL TOPLAMI SATIRI (EN ALT) -->
                    <tr class="total-row">
                        <td class="no-cell">-</td>
                        <td class="question-cell"><strong>Genel Toplam</strong></td>

                        <template v-for="colGroup in columnHierarchy">
                            <td v-for="optValue in colGroup.options"
                                :key="'total_' + colGroup.categoryKey + '_' + optValue" class="col-total-cell">
                                <strong>{{ calculateOptionMetric('ALL_QUESTIONS', colGroup.categoryKey, optValue)
                                    }}</strong>
                            </td>
                        </template>

                        <!-- MATRİSİN SAĞ ALT KÖŞE TOPLAMI -->
                        <td class="grand-total-cell">
                            <strong>{{ calculateOptionMetric('ALL_QUESTIONS', 'ALL_CATEGORIES', 'ALL') }}</strong>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import FormRaporService from '@/services/FormRaporService'

export default {
    name: 'SurveyMatrixPivotWithTotals',
    components: {
        draggable
    },
    data() {
        return {
            selectedAggregator: 'AVG',
            rowSortOrder: 'NONE',
            colSortOrder: 'NONE',

            // Sürükleme Havuzu
            availableFields: [
                //     { key: 'Çalıştığınız Birim ?', label: 'Çalıştığınız Birim ?' },
                //     { key: 'Eğitim sırasında soru sorup görüşlerimi rahatlıkla ifade edebildim.', label: 'Görüş Bildirme Kolaylığı' }
            ],

            rowFields: [],
            colFields: [],

            surveyData: [
                // {
                //   "KayitId": "f8e4526f-7a3e-4460-9a0d-be37014fa3bc",
                //   "Eğitim sırasında soru sorup görüşlerimi rahatlıkla ifade edebildim.": { "value": "Katılıyorum", "displayValue": "4" },
                //   "Çalıştığınız Birim ?": { "value": "İdari Personel", "displayValue": "2" }
                // },
                // {
                //   "KayitId": "e2c3116a-1b2c-4810-8b1a-ca52014fb1aa",
                //   "Eğitim sırasında soru sorup görüşlerimi rahatlıkla ifade edebildim.": { "value": "Kesinlikle Katılıyorum", "displayValue": "5" },
                //   "Çalıştığınız Birim ?": { "value": "Akademik Personel", "displayValue": "1" }
                // },
                // {
                //   "KayitId": "a1b2c3d4-5e6f-7a8b-9c0d-1e2f3a4b5c6d",
                //   "Eğitim sırasında soru sorup görüşlerimi rahatlıkla ifade edebildim.": { "value": "Katılıyorum", "displayValue": "4" },
                //   "Çalıştığınız Birim ?": { "value": "İdari Personel", "displayValue": "2" }
                // }
            ]
        };
    },
    computed: {
        // 1. SATIR SIRALAMA VEYA VARSAYILAN TEK SATIR
        processedRowFields() {
            // Satır boş ise tüm soruları kapsayan sanal bir satır oluştur
            if (this.rowFields.length === 0) {
                return [{ key: 'ALL_QUESTIONS', label: 'Tüm Sorular / Genel' }];
            }

            var list = this.rowFields.slice();
            if (this.rowSortOrder === 'ASC') {
                return list.sort(function (a, b) { return a.label.localeCompare(b.label, 'tr'); });
            } else if (this.rowSortOrder === 'DESC') {
                return list.sort(function (a, b) { return b.label.localeCompare(a.label, 'tr'); });
            }
            return list;
        },

        // 2. SÜTUN HİYERARŞİSİ VEYA VARSAYILAN TEK SÜTUN
        columnHierarchy() {
            var self = this;

            // Sütun boş ise tüm kayıtları kapsayan sanal bir sütun oluştur
            if (this.colFields.length === 0) {
                return [{
                    categoryKey: 'ALL_CATEGORIES',
                    categoryLabel: 'Tüm Kayıtlar',
                    options: ['Genel']
                }];
            }

            return this.colFields.map(function (cField) {
                var rawOptions = self.surveyData.map(function (row) {
                    return row[cField.key] && row[cField.key].value ? row[cField.key].value : null;
                });

                var uniqueOptions = rawOptions.filter(function (val, index, arr) {
                    return val !== null && arr.indexOf(val) === index;
                });

                if (self.colSortOrder === 'ASC') {
                    uniqueOptions.sort(function (a, b) { return a.localeCompare(b, 'tr'); });
                } else if (self.colSortOrder === 'DESC') {
                    uniqueOptions.sort(function (a, b) { return b.localeCompare(a, 'tr'); });
                }

                return {
                    categoryKey: cField.key,
                    categoryLabel: cField.label,
                    options: uniqueOptions
                };
            });
        }
    },
    created() {
        this.dataBind();
    },
    methods: {
 isGUID(str) {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return guidRegex.test(str);
},

        dataBind() {
if (!this.isGUID(this.$route.params.id)) {
        this.$toast.warning("Lütfen geçerli bir id parametresi giriniz.")
        return false;
}
try {
      FormRaporService.RaporByFormId(this.$route.params.id).then((result) => {
                this.surveyData = result.data;
                let veriler = result.data;
                let m = veriler[0];
                let yeniList = [];

                for (let key in m) {
                    let yeni = {};
                    if (m[key] !== null && typeof (m[key]) == "object") {
                        yeni["key"] = key;
                        yeni["label"] = key;


                    }
                    else {
                        yeni["key"] = key;
                        yeni["label"] = key;

                    }
                    yeniList.push(yeni);

                }



                this.availableFields = yeniList;
            });
} catch (error) {
    this.$toast.warning("Hata Oluştu.")
}

          


        },
        toggleRowSort(order) {
            this.rowSortOrder = this.rowSortOrder === order ? 'NONE' : order;
        },

        // HESAPLAMA MOTORU (DİNAMİK BOŞ SATIR/SÜTUN VE TOPLAM DESTEKLİ)
        calculateOptionMetric(questionKey, categoryKey, targetOptionValue) {
            var self = this;

            // 1. ADIM: Sütun/Kategori Filtrelemesi
            var filteredRows = this.surveyData;
            if (categoryKey !== 'ALL_CATEGORIES' && targetOptionValue !== 'ALL') {
                filteredRows = this.surveyData.filter(function (row) {
                    return row[categoryKey] && row[categoryKey].value === targetOptionValue;
                });
            }

            if (!filteredRows.length) return '0';

            // 2. ADIM: Soru / Puan Değerlerinin Çekilmesi
            var valuesArray = [];

            if (questionKey === 'ALL_QUESTIONS') {
                // Satır boşsa veya Genel Toplam satırıysa tüm soruların/objelerin puanlarını topla
                filteredRows.forEach(function (row) {
                    Object.keys(row).forEach(function (k) {
                        if (row[k] && typeof row[k] === 'object' && row[k].displayValue) {
                            valuesArray.push(Number(row[k].displayValue) || 0);
                        }
                    });
                });
            } else {
                // Tek bir soru seçildiyse ilgili sorunun puanını al
                valuesArray = filteredRows.map(function (row) {
                    var qObj = row[questionKey];
                    return qObj && qObj.displayValue ? Number(qObj.displayValue) : 0;
                });
            }

            if (!valuesArray.length) return '0';

            var sum = valuesArray.reduce(function (a, b) { return a + b; }, 0);

            // 3. ADIM: Metrik Hesaplama
            switch (this.selectedAggregator) {
                case 'AVG':
                    return (sum / valuesArray.length).toFixed(2).replace('.', ',');
                case 'COUNT':
                    // COUNT seçildiğinde eğer kayıt sayısı isteniyorsa satır sayısını dön
                    return questionKey === 'ALL_QUESTIONS' && categoryKey !== 'ALL_CATEGORIES'
                        ? filteredRows.length
                        : valuesArray.length;
                case 'SUM':
                    return sum;
                case 'MAX':
                    return Math.max.apply(null, valuesArray);
                case 'MIN':
                    return Math.min.apply(null, valuesArray);
                default:
                    return (sum / valuesArray.length).toFixed(2).replace('.', ',');
            }
        }
    }
};
</script>

<style scoped>
.pivot-builder {
    padding: 20px;
    font-family: Arial, sans-serif;
}

.control-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    background-color: #f1f3f5;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 20px;
    align-items: center;
}

.control-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.control-group select {
    padding: 6px 10px;
    border-radius: 4px;
    border: 1px solid #ced4da;
}

.sort-btn {
    padding: 5px 10px;
    border: 1px solid #adb5bd;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
}

.sort-btn.active {
    background-color: #0d6efd;
    color: white;
    border-color: #0d6efd;
}

.clear-sort-btn {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    font-size: 12px;
    text-decoration: underline;
}

.builder-panels {
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
}

.panel {
    flex: 1;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 12px;
    position: relative;
}

.panel h4 {
    margin-top: 0;
    margin-bottom: 10px;
    font-size: 14px;
    color: #495057;
}

.drag-area {
    min-height: 90%;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.dropzone {
    border: 2px dashed #ced4da;
    border-radius: 6px;
    padding: 8px;
    background: #ffffff;
}

.field-chip {
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    cursor: grab;
    user-select: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.field-chip.available {
    background-color: #e2e3e5;
    color: #383d41;
}

.field-chip.row-chip {
    background-color: #f8d7da;
    color: #721c24;
}

.field-chip.col-chip {
    background-color: #d4edda;
    color: #155724;
}

.empty-hint {
    position: absolute;
    top: 55px;
    left: 0;
    right: 0;
    font-size: 12px;
    color: #aaa;
    text-align: center;
    pointer-events: none;
}

.table-container {
    overflow-x: auto;
    margin-top: 15px;
}

.pivot-matrix-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.pivot-matrix-table th,
.pivot-matrix-table td {
    border: 1px solid #b2bec3;
    padding: 8px;
    text-align: center;
}

.top-category-header {
    background-color: #d63031;
    color: white;
    font-weight: bold;
}

.sub-category-header {
    background-color: #e17055;
    color: white;
    font-size: 12px;
}

.corner-header,
.total-header {
    background-color: #d63031;
    color: white;
    font-weight: bold;
}

.question-cell {
    text-align: left !important;
    background-color: #f5f6fa;
    min-width: 200px;
}

.no-cell {
    background-color: #f5f6fa;
    font-weight: bold;
    width: 40px;
}

.data-cell {
    background-color: #ffffff;
}

.row-total-cell,
.col-total-cell {
    background-color: #ffeaa7;
}

.grand-total-cell {
    background-color: #fdcb6e;
}

.total-row {
    background-color: #ffeaa7;
}
</style>